import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Alert, AlertTitle, Typography } from '@mui/material';
import DetectedKeysTable from './DetectedKeysTable';
import QuestionFormModal from './QuestionFormModal';
import { getValidKeys, isStaticTemplate, generateDisplayTextFromKey } from '../../utils/templateKeyUtils';

const QuestionsTab = ({ content = '', questions = [], onChange }) => {
  const validKeys = useMemo(() => {
    return getValidKeys(content);
  }, [content]);

  const isStatic = useMemo(() => {
    return isStaticTemplate(content);
  }, [content]);

  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const handleAddQuestion = (key) => {
    const newQuestion = {
      _tempId: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      key: key,
      displayText: generateDisplayTextFromKey(key),
      type: 'Text',
      required: true,
      answers: []
    };
    
    setEditingQuestion(newQuestion);
    setShowModal(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setShowModal(true);
  };

  const handleSaveQuestion = (savedQuestion) => {    
    // Key-based matching: each key should have only one question
    const existingIndex = questions.findIndex(
      q => q.key === savedQuestion.key
    );
    
    if (existingIndex >= 0) {
      // Update existing question with same key
      const updated = [...questions];
      const existingQuestion = questions[existingIndex];
      
      // CRITICAL: Preserve answers and nestedQuestions from existing question if savedQuestion doesn't have them
      // This prevents nested questions from being lost when user only updates other fields
      const preservedAnswers = savedQuestion.answers !== undefined 
        ? savedQuestion.answers 
        : existingQuestion.answers;
      
      // IMPORTANT: Spread savedQuestion first to ensure all fields (including defaultValue) are included
      // But preserve answers if they exist in savedQuestion, otherwise keep existing ones
      updated[existingIndex] = {
        ...savedQuestion,
        _id: existingQuestion._id || savedQuestion._id,
        _tempId: existingQuestion._tempId || savedQuestion._tempId,
        // Preserve answers (which includes nestedQuestions) if savedQuestion doesn't have them
        answers: preservedAnswers,
      };
      
      
      onChange(updated);
    } else {
      // Add new question
      onChange([...questions, savedQuestion]);
    }
    
    setShowModal(false);
    setEditingQuestion(null);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      {isStatic && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Statik Şablon</AlertTitle>
          <Typography variant="body2">
            Bu şablon statik içerik. Hiç {'{{key}}'} bulunmadığı için soru tanımı opsiyonel.
            İsterseniz soru tanımlamadan şablonu kaydedebilirsiniz.
          </Typography>
        </Alert>
      )}

      <DetectedKeysTable
        validKeys={validKeys}
        questions={questions}
        onAdd={(key) => handleAddQuestion(key)}
        onEdit={(question) => handleEditQuestion(question)}
      />

      <QuestionFormModal
        open={showModal}
        question={editingQuestion}
        parentContext={null}
        allQuestions={questions}
        onClose={() => {
          setShowModal(false);
          setEditingQuestion(null);
        }}
        onSave={handleSaveQuestion}
      />
    </Box>
  );
};

QuestionsTab.propTypes = {
  content: PropTypes.string,
  questions: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

QuestionsTab.defaultProps = {
  content: '',
  questions: [],
};

export default QuestionsTab;
